/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import {
  ButtonContainer,
  Heading,
  Main,
  PromptList,
  SectionLeft,
  SectionRight,
} from './promptSelectPage.style';
import { Button, Textarea } from '../../../components/uielements';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPrecedures, verifyProcedurePermit } from '../../../services/procedureService';

import backgroundImg from '../../../assets/PNG/prompt-select-page.png';
import backgroundImgCompress from '../../../assets/PNG/prompt-select-page-compress.png';
import useImageLoader from '../../../hooks/useImageLoader';
import { IntlMessages } from '../../../helper/langHelper';
import { clearItem, getItem, setItem } from '../../../helper/localStorageHelper';
import { clearCache, clearSummaryCache } from '../../../helper/cacheHelper';
import { useIntl } from 'react-intl';
import InvalidProcedureMessageModal from './procedureInvalidMessageModal';
import ZoneErrorModal from './zoneErrorModal';
import ZonePrecinctSelectionModal from './zonePrecinctSelectionModal';
import { getZoneDetails } from '../../../services/getSummary';
import SearchableSelect from '../../../components/uielements/selectComponent/searchableSelect';

const PromptSelect = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // State management
  const [inputValue, setInputValue] = useState('');
  const [selectedProcedure, setSelectedProcedure] = useState();
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showZoneErrorModal, setShowZoneErrorModal] = useState(false);
  const [showZoneSelectionModal, setShowZoneSelectionModal] = useState(false);
  const [zoneDetailsData, setZoneDetailsData] = useState(null);
  const [manualZoneSelection, setManualZoneSelection] = useState(null);

  // Location state
  const searchAddress = location.state?.searchAddress || '';
  const fullName = location.state?.fullName || '';
  const phone = location.state?.phone || '';
  const email = location.state?.email || '';
  const flow = searchParams.get('flow');

  const promptSelectBg = useImageLoader(backgroundImgCompress, backgroundImg);
  const procedureFromStorage = getItem('procedure');

  // Fetch zone details
  const fetchZoneDetails = async () => {
    if (!searchAddress) return;

    try {
      setLoading(true);
      setError('');
      const payload = { propertyAddress: searchAddress?.address };
      const response = await getZoneDetails(payload);
      setZoneDetailsData(response);
    } catch (error) {
      setError('Failed to load zone details. Please try again later.');
      console.error('Error fetching zone details:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch procedures
  const getProcedure = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getPrecedures();
      setProcedures(response);
    } catch (error) {
      setError('Failed to load procedures. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProcedure();
    fetchZoneDetails();
  }, []);

  const handlePromptClick = (value) => {
    setSelectedProcedure(value);
  };

  const handleDescriptionChange = (event) => {
    setInputValue(event.target.value);
  };

  const navigateToNextStep = (finalZoneData) => {
    navigate(`/mapView${flow ? `?flow=${flow}` : ''}`, {
      state: {
        address: searchAddress,
        fullName,
        email,
        phone,
        selectedProcedure,
        inputValue,
        zoneDetailsData: finalZoneData,
      },
    });
  };

  /**
   * Verify procedure permit with given zone data
   * Handles both automatic and manual zone selection
   */
  const verifyProcedureWithZoneData = async (zoneData) => {
    const verificationPromises = [];

    // First verification with primary zone
    const payloadOne = {
      regionName: zoneData?.zoneName || '',
      precinctName: zoneData?.zonePrecinctName || '',
      procedureId: selectedProcedure?._id,
    };

    verificationPromises.push(verifyProcedurePermit(payloadOne));

    // Second verification with local plan (if available)
    if (zoneData?.localPlanName && zoneData.localPlanName.trim() !== '') {
      const payloadTwo = {
        regionName: zoneData.localPlanName,
        precinctName: zoneData.localPlanPrecinctName || '',
        procedureId: selectedProcedure?._id,
      };
      verificationPromises.push(verifyProcedurePermit(payloadTwo));
    }

    // Execute all verification calls in parallel
    const verificationResults = await Promise.all(verificationPromises);

    // Store verification results
    const verificationData = {
      results: verificationResults,
      hasMultipleVerifications: verificationPromises.length > 1,
    };

    setItem('procedurePermit', verificationData);

    // Check if any verification passed
    return verificationResults.some((result) => result.procedurePermit === true);
  };

  /**
   * Handle manual zone and precinct selection from modal
   */
  const handleManualZoneSelection = async (selectedZone, selectedPrecinct) => {
    try {
      setLoading(true);
      setError('');

      // Create zone data structure matching automatic fetch format
      const manualZoneData = {
        zoneName: selectedZone.regionName || selectedZone.name,
        zonePrecinctName: selectedPrecinct.precinctName || selectedPrecinct.name,
        localPlanName: '', // Manual selection doesn't have local plan
        localPlanPrecinctName: '',
        // Store original IDs for reference
        zoneId: selectedZone._id,
        precinctId: selectedPrecinct._id,
      };

      // Store manual selection for potential future use
      setManualZoneSelection(manualZoneData);

      // Close the selection modal
      setShowZoneSelectionModal(false);

      // Verify procedure with manually selected zone/precinct
      const hasPermittedProcedure = await verifyProcedureWithZoneData(manualZoneData);

      if (hasPermittedProcedure) {
        // Navigate with manual zone data
        navigateToNextStep(manualZoneData);
        return;
      }

      // Show invalid procedure modal if verification failed
      setShowModal(true);
    } catch (error) {
      setError('Failed to verify procedure with selected zone and precinct.');
      console.error('Error in handleManualZoneSelection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Validation checks
    if (!selectedProcedure) {
      setError('Please select a procedure before proceeding.');
      return;
    }

    if (!inputValue.trim()) {
      setError('Please enter a description before proceeding.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Clear cache if procedure changed
      if (procedureFromStorage && procedureFromStorage !== selectedProcedure) {
        clearCache();
        clearSummaryCache();
        clearItem('applicationId');
      }

      setItem('procedure', selectedProcedure);

      // Check if zone details are available
      if (!zoneDetailsData) {
        setError('Zone details not available. Please refresh the page.');
        return;
      }

      // Check if zoneName is missing - trigger manual selection
      if (!zoneDetailsData.zoneName || zoneDetailsData.zoneName.trim() === '') {
        setShowZoneSelectionModal(true);
        return;
      }

      // Proceed with automatic zone verification
      const hasPermittedProcedure = await verifyProcedureWithZoneData(zoneDetailsData);

      if (hasPermittedProcedure) {
        navigateToNextStep(zoneDetailsData);
        return;
      }

      // Show invalid procedure modal if verification failed
      setShowModal(true);
    } catch (error) {
      setError("Sorry, we couldn't locate any records with your selected options!");
      console.error('Error in handleNext:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate('/');
  };

  const handleZoneErrorModalClose = () => {
    setShowZoneErrorModal(false);
    navigate('/');
  };

  const handleZoneSelectionModalClose = () => {
    setShowZoneSelectionModal(false);
    // Optionally navigate back or stay on page
  };

  return (
    <Main role="main">
      <SectionLeft className="hide-scroll-bar">
        <Heading role="heading" aria-level="1">
          <IntlMessages id="prompt.heading" />
        </Heading>

        {error && (
          <div
            role="alert"
            aria-live="polite"
            style={{
              color: '#d32f2f',
              padding: '10px',
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <PromptList role="listbox" aria-labelledby="procedureInstructions" aria-busy={loading}>
          <SearchableSelect
            options={procedures}
            selectedOption={handlePromptClick}
            label={<IntlMessages id="prompt.secondary.heading" />}
            placeholder={<IntlMessages id="question.one" />}
            width="250px"
            sizevalue="medium"
            loading={loading}
            id="procedure-selector"
          />
        </PromptList>

        <div>
          <label
            htmlFor="enter-description"
            style={{ display: 'inline-block', marginBottom: '10px' }}
          >
            <IntlMessages id="question.two" />
            <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
            <br />
            <span
              style={{
                fontSize: '12px',
                color: '#5c5b5b',
                fontWeight: 'normal',
                lineHeight: '1.4',
              }}
            >
              (<IntlMessages id="question.three" />)
            </span>
          </label>
          <Textarea
            id="enter-description"
            value={inputValue}
            aria-label="Describe here"
            aria-required="true"
            required
            placeholder={intl.formatMessage({ id: 'describe.here' })}
            onChange={handleDescriptionChange}
          />
        </div>

        <ButtonContainer>
          <Button
            onClick={handleNext}
            disabled={!selectedProcedure || !inputValue.trim() || loading}
            aria-disabled={!selectedProcedure || !inputValue.trim() || loading}
            aria-label={
              selectedProcedure && inputValue.trim()
                ? 'Proceed to next step'
                : 'Please select a procedure and enter a description before proceeding'
            }
          >
            {loading ? 'Processing...' : <IntlMessages id="button.label" />}
          </Button>
        </ButtonContainer>
      </SectionLeft>
      <SectionRight aria-hidden="true" bg={promptSelectBg} />

      {/* Modal for procedure not permitted */}
      <InvalidProcedureMessageModal
        isModalOpen={showModal}
        onClose={handleModalClose}
        zone={
          manualZoneSelection?.zoneName || zoneDetailsData?.zoneName || 'Rural Residential Zone'
        }
      />

      {/* Modal for zone error (legacy - kept for backward compatibility) */}
      <ZoneErrorModal
        isModalOpen={showZoneErrorModal}
        onClose={handleZoneErrorModalClose}
        address={searchAddress?.address || ''}
      />

      {/* Modal for manual zone/precinct selection */}
      <ZonePrecinctSelectionModal
        isOpen={showZoneSelectionModal}
        onClose={handleZoneSelectionModalClose}
        onConfirm={handleManualZoneSelection}
        procedureId={selectedProcedure?._id}
      />
    </Main>
  );
};

export default PromptSelect;
